import React, { useEffect, useState } from 'react';
import { Card, Table, DatePicker, Space } from 'antd';
import type { TableProps } from 'antd';
import { getAdvertiserDataAnalysis } from '@/api/modules/Data';
import type { AdvertiserDataAnalysisItem, PageResponse } from '@/api/interface/data';
import type { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

const DataAnalysis: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<AdvertiserDataAnalysisItem[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
        dayjs().subtract(7, 'day'),
        dayjs()
    ]);

    const columns: TableProps<AdvertiserDataAnalysisItem>['columns'] = [
        {
            title: '广告账户ID',
            dataIndex: 'advId',
            key: 'advId',
        },
        {
            title: '广告账户名称',
            dataIndex: 'advName',
            key: 'advName',
        },
        {
            title: '花费',
            dataIndex: 'spend',
            key: 'spend',
            render: (value) => value?.toFixed(2) ?? '-'
        },
        {
            title: '展示次数',
            dataIndex: 'impressions',
            key: 'impressions',
            render: (value) => value?.toLocaleString() ?? '-'
        },
        {
            title: '点击次数',
            dataIndex: 'clicks',
            key: 'clicks',
            render: (value) => value?.toLocaleString() ?? '-'
        },
        {
            title: '到达率',
            dataIndex: 'reach',
            key: 'reach',
            render: (value) => value?.toLocaleString() ?? '-'
        },
        {
            title: '频次',
            dataIndex: 'frequency',
            key: 'frequency',
            render: (value) => value?.toFixed(2) ?? '-'
        },
        {
            title: '链接点击',
            dataIndex: 'inlineLinkClicks',
            key: 'inlineLinkClicks',
            render: (value) => value?.toLocaleString() ?? '-'
        },
        {
            title: '帖子互动',
            dataIndex: 'inlinePostEngagement',
            key: 'inlinePostEngagement',
            render: (value) => value?.toLocaleString() ?? '-'
        }
    ];

    const fetchData = async (
        page: number = pagination.current,
        pageSize: number = pagination.pageSize
    ) => {
        try {
            setLoading(true);
            const pageData = await getAdvertiserDataAnalysis({
                current: page,
                pageSize: pageSize,
                startTime: dateRange[0].format('YYYY-MM-DD'),
                endTime: dateRange[1].format('YYYY-MM-DD')
            });
            
            setData(pageData.list);
            setPagination({
                ...pagination,
                current: pageData.current,
                total: pageData.total,
                pageSize: pageData.size
            });
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleTableChange: TableProps<AdvertiserDataAnalysisItem>['onChange'] = (
        pagination,
        filters,
        sorter
    ) => {
        fetchData(pagination.current, pagination.pageSize);
    };

    const handleDateRangeChange: RangePickerProps['onChange'] = (dates) => {
        if (dates && dates[0] && dates[1]) {
            setDateRange([dates[0], dates[1]]);
            fetchData(1); // 重置到第一页
        }
    };

    return (
        <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <div>
                    <RangePicker
                        value={dateRange}
                        onChange={handleDateRangeChange}
                        allowClear={false}
                    />
                </div>
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="advId"
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条`
                    }}
                    loading={loading}
                    onChange={handleTableChange}
                />
            </Space>
        </Card>
    );
};

export default DataAnalysis;
